/**
 * Which per-championship stat the ranking is computed on. Stable identifiers:
 * they are persisted in `media_manager_templates.player_ranking`.
 *
 * This dimension used to be the create-time «Показатель» subtype
 * (`IndicatorSubtype`). It moved here because a template may show several stats
 * at once, so only the *ranking* needs to name one.
 */
export const PlayerRankingStat = {
  GAMES: 'games',
  GOALS: 'goals',
  ASSISTS: 'assists',
  GOALS_ASSISTS: 'goals_assists',
  YELLOW_CARDS: 'yellow_cards',
  RED_CARDS: 'red_cards',
} as const;

export type PlayerRankingStat = typeof PlayerRankingStat[keyof typeof PlayerRankingStat];

export type PlayerRankingDirection = 'desc' | 'asc';

export interface PlayerRankingSettings {
  stat: PlayerRankingStat;
  direction: PlayerRankingDirection;
  /** Max rank slots to render. Undefined = every player with a stats line. */
  limit?: number;
}

export const DEFAULT_PLAYER_RANKING: PlayerRankingSettings = {
  stat: PlayerRankingStat.GOALS,
  direction: 'desc',
};

const VALID_STAT: ReadonlySet<string> = new Set<string>(Object.values(PlayerRankingStat));
const VALID_DIRECTION: ReadonlySet<PlayerRankingDirection> = new Set<PlayerRankingDirection>([
  'desc',
  'asc',
]);

/**
 * Tolerant normalizer — same role and shape as `normalizeMatchLayout`. Takes an
 * unknown blob (a jsonb column read straight off the row) and fills the default
 * for anything missing or unrecognized, so no caller has to defend against a
 * hand-edited or older-shaped value.
 */
export function normalizePlayerRanking(raw: unknown): PlayerRankingSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PLAYER_RANKING };
  const r = raw as Record<string, unknown>;

  const stat =
    typeof r.stat === 'string' && VALID_STAT.has(r.stat)
      ? (r.stat as PlayerRankingStat)
      : DEFAULT_PLAYER_RANKING.stat;
  const direction =
    typeof r.direction === 'string' && VALID_DIRECTION.has(r.direction as PlayerRankingDirection)
      ? (r.direction as PlayerRankingDirection)
      : DEFAULT_PLAYER_RANKING.direction;

  // A limit below 1 would render nothing at all; treat it as "no limit" rather
  // than silently producing an empty image.
  const limit =
    typeof r.limit === 'number' && Number.isFinite(r.limit) && r.limit >= 1
      ? Math.floor(r.limit)
      : undefined;

  return limit === undefined ? { stat, direction } : { stat, direction, limit };
}

// `orderBy` values are matched case- and separator-insensitively: the URL param
// `yellowcards`, `yellow-cards`, `Yellow_Cards` all collapse to the canonical
// `yellow_cards`. The map is keyed by each stat id stripped to bare letters.
const STAT_BY_NORMALIZED: ReadonlyMap<string, PlayerRankingStat> = new Map(
  Object.values(PlayerRankingStat).map((s) => [s.replace(/[^a-z]/g, ''), s]),
);

function parseStatOverride(raw: string | null): PlayerRankingStat | undefined {
  if (!raw) return undefined;
  return STAT_BY_NORMALIZED.get(raw.toLowerCase().replace(/[^a-z]/g, ''));
}

function parseDirectionOverride(raw: string | null): PlayerRankingDirection | undefined {
  if (!raw) return undefined;
  const v = raw.toLowerCase();
  return VALID_DIRECTION.has(v as PlayerRankingDirection) ? (v as PlayerRankingDirection) : undefined;
}

function parseLimitOverride(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  // Invalid (non-numeric, <1) means "no override" — a garbage param must not
  // wipe the template's own limit. Omit `top` entirely to inherit the template's.
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : undefined;
}

/**
 * Layers render-link query overrides onto a base ranking (the template's own
 * `player_ranking`), letting one template be rendered by a different stat /
 * order / slot count without editing it. Only params that are present **and**
 * valid override; anything absent or unrecognized leaves the base value
 * untouched — so `?order=asc` alone flips the direction while keeping the
 * template's stat and limit.
 *
 * Param contract (also documented in docs/media-manager-render-links.md):
 *   - `orderBy` → stat       games | goals | assists | goals_assists | yellow_cards | red_cards
 *   - `order`   → direction  asc | desc
 *   - `top`     → limit      positive integer (slot count); omit to inherit the template's
 *
 * `base` is assumed already normalized (it is — see `normalizePlayerRanking`),
 * so the merged result is always a valid `PlayerRankingSettings`.
 */
export function applyPlayerRankingOverrides(
  base: PlayerRankingSettings,
  params: URLSearchParams,
): PlayerRankingSettings {
  const merged: PlayerRankingSettings = { ...base };

  const stat = parseStatOverride(params.get('orderBy'));
  if (stat) merged.stat = stat;

  const direction = parseDirectionOverride(params.get('order'));
  if (direction) merged.direction = direction;

  const limit = parseLimitOverride(params.get('top'));
  if (limit !== undefined) merged.limit = limit;

  return merged;
}
